import { useDebouncedValue, useLocalStorage } from "@mantine/hooks"
import {
  Cardholder,
  CheckCircle,
  Circle,
  CurrencyInr,
  DotsThreeVertical,
  Hand,
  Info,
  List,
  MagnifyingGlass,
  PaperPlane,
  Receipt,
  Wallet,
} from "@phosphor-icons/react"
import {
  Avatar,
  CodenameTag,
  InputGroup,
  Menu,
  MenuItem,
  Note,
  Popover,
  SegmentedControl,
  Tag,
  Tooltip,
} from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useAuthorization } from "~/components/AuthorizationProvider"
import { CustomToast } from "~/components/CustomToast"
import { RemarkModal } from "~/components/Remark/RemarkModal"
import { TableSummary } from "~/components/TableSummary"
import TradeKindTag from "~/components/TradeKindTag"
import ADVANCE_STATUS from "~/constants/AdvanceStatus"
import FilterTypes from "~/constants/FilterTypes"
import { TradeKinds } from "~/constants/Kinds"
import { PayableReceivableDate } from "~/constants/Payable"
import { Permission } from "~/constants/Permissions"
import { siteConfig } from "~/constants/config"
import { AppRoutes } from "~/constants/routes"
import { TRADE_KINDS } from "~/constants/static.items"
import {
  Order_By,
  useAdvancePaymentDetailQuery,
  useAdvanceSummaryDashboardQuery,
  useAllAdvancePaymentsQuery,
  useAuthenticatedUserQuery,
  useCloseAdvanceRequestMutation,
  useDepartmentUserAdvancesQuery,
  useReceiptAdvanceSummaryDashboardQuery,
  useUpdateAdvancePaymentMutation,
} from "~/generated/graphql"
import { AddTransaction } from "~/pages/d/payable/advance/AddTransaction"
import { CloseAdvanceRequest } from "~/pages/d/payable/advance/CloseRequest"
import PayableActions from "~/pages/d/payable/advance/PayableActions"
import PayableFilter, {
  PayableFilterType,
} from "~/pages/d/payable/advance/PayableFilter"
import {
  PayableSelector,
  usePayableSelector,
} from "~/pages/d/payable/advance/PayableSelector"
import { config } from "~/pages/pavti/config"
import { inrFormatter } from "~/utils/inrFormatter"
import { useDepartment } from "~/utils/useDepartment"

const AdvancePayable = () => {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { selected } = usePayableSelector()
  const isTrade = selected === "trade"
  const isUserWise = selected === "user"
  const isReceipt = selected === "receipt"
  const search = params.get("search")
  const advanceId = params.get("advanceId")
  const [debouncedSearch] = useDebouncedValue(
    search?.trim(),
    siteConfig.debounce
  )

  const [filter, setFilter] = useState<PayableFilterType>(() => {
    const savedFilter = localStorage.getItem(FilterTypes.advancePayableFilter)
    return savedFilter ? JSON.parse(savedFilter) : {}
  })

  useEffect(() => {
    localStorage.setItem(
      FilterTypes.advancePayableFilter,
      JSON.stringify(filter)
    )
  }, [filter])

  const [closeAdvancePayment, setCloseAdvancePayment] = useState<any>()
  const [, closeAdvance] = useCloseAdvanceRequestMutation()

  const page = Number(params.get("page")) || 1
  const [department] = useLocalStorage({ key: "department" })
  const isShowImporter = useAuthorization(Permission.canUploadPayableReceivable)
  const canViewPayableReceivableReport = useAuthorization(
    Permission.ViewPayableReceivableReport
  )

  const [{ data: authData }] = useAuthenticatedUserQuery()
  const me = authData?.me
  const { id: sharedDeptId } = useDepartment()
  const departmentCodename =
    (department && String(department)) ||
    me?.agent?.current_department?.codename

  const [{ data: userWiseData, fetching: userWiseFetching }] =
    useDepartmentUserAdvancesQuery({
      variables: {
        filters: {
          department_codename: departmentCodename,
          user_ref: filter?.seller?.codename,
        },
      },
      pause: !isUserWise || !departmentCodename,
      requestPolicy: "network-only",
    })

  const summaryVariables = {
    department_id: sharedDeptId,
    sellerId: filter?.seller?.codename,
    createdFrom: filter?.date_from
      ? dayjs(filter.date_from).format("YYYY-MM-DD")
      : dayjs(PayableReceivableDate).format("YYYY-MM-DD"),
    createdTill: filter?.date_to
      ? dayjs(filter.date_to).format("YYYY-MM-DD")
      : undefined,
  }

  const [{ data: summaryData, fetching: tradeSummaryFetching }] =
    useAdvanceSummaryDashboardQuery({
      variables: summaryVariables,
      pause: !sharedDeptId || isReceipt,
      requestPolicy: "network-only",
    })

  const [{ data: receiptSummaryData, fetching: receiptSummaryFetching }] =
    useReceiptAdvanceSummaryDashboardQuery({
      variables: summaryVariables,
      pause: !sharedDeptId || !isReceipt,
      requestPolicy: "network-only",
    })

  const summaryFetching = tradeSummaryFetching || receiptSummaryFetching

  const [{ data: detailData, fetching: detailFetching }] =
    useAdvancePaymentDetailQuery({
      variables: {
        id: advanceId,
      },
      pause: !advanceId,
      requestPolicy: "network-only",
    })
  const [{ data, fetching }, refetch] = useAllAdvancePaymentsQuery({
    variables: {
      limit: config.pageSize,
      offset: (page - 1) * config.pageSize,
      orderBy: {
        created_at: Order_By.DescNullsLast,
      },
      where: {
        status: {
          _neq: "Draft",
        },
        created_at: {
          _lte: filter?.date_to ? dayjs(filter?.date_to).endOf("d") : undefined,
          _gte: filter?.date_from
            ? dayjs(filter?.date_from).startOf("d")
            : undefined,
          _gt: dayjs(PayableReceivableDate).startOf("d"),
        },

        ...(filter?.placed_by
          ? {
              created_by_id: {
                _eq: filter?.placed_by?.id?.toString(),
              },
            }
          : undefined),
        is_verified: filter?.advance_status?.id
          ? filter?.advance_status?.id === "verified"
            ? {
                _eq: true,
              }
            : filter?.advance_status?.id === "rejected"
            ? {
                _eq: false,
              }
            : { _is_null: true }
          : undefined,
        parameter: filter?.advance_type
          ? {
              name: {
                _ilike: filter?.advance_type,
              },
            }
          : undefined,
        settlement_status: {
          _eq:
            filter?.transaction_status?.id === "pending"
              ? "Not Started"
              : filter?.transaction_status?.id === "settled"
              ? "Complete"
              : filter?.transaction_status?.id === "partial"
              ? "Partial"
              : undefined,
        },
        trade: isTrade
          ? {
              kind: filter?.patti_type
                ? {
                    _eq: filter?.patti_type,
                  }
                : {
                    _in: [TradeKinds.SaudaPavti, TradeKinds.Booking],
                  },
              department: {
                reference_id: {
                  _eq:
                    (department && String(department)) ||
                    me?.agent?.current_department?.codename,
                },
              },
              ...(debouncedSearch
                ? {
                    id:
                      debouncedSearch && Number(debouncedSearch)
                        ? {
                            _eq: Number(debouncedSearch),
                          }
                        : undefined,
                  }
                : undefined),

              seller:
                (debouncedSearch && !Number(debouncedSearch)) || filter?.seller
                  ? {
                      name:
                        debouncedSearch && !Number(debouncedSearch)
                          ? {
                              _ilike: `%${debouncedSearch}%`,
                            }
                          : undefined,
                      reference_id: filter?.seller
                        ? {
                            _eq: filter?.seller?.codename,
                          }
                        : undefined,
                    }
                  : undefined,
            }
          : undefined,
        receipt: !isTrade
          ? {
              kind: filter?.patti_type
                ? {
                    _eq: filter?.patti_type,
                  }
                : {
                    _in: [TradeKinds.SaudaPavti, TradeKinds.Booking],
                  },
              department: {
                reference_id: {
                  _eq:
                    (department && String(department)) ||
                    me?.agent?.current_department?.codename,
                },
              },
              ...(debouncedSearch
                ? {
                    id:
                      debouncedSearch && Number(debouncedSearch)
                        ? {
                            _eq: Number(debouncedSearch),
                          }
                        : undefined,
                  }
                : undefined),

              seller:
                (debouncedSearch && !Number(debouncedSearch)) || filter?.seller
                  ? {
                      name:
                        debouncedSearch && !Number(debouncedSearch)
                          ? {
                              _ilike: `%${debouncedSearch}%`,
                            }
                          : undefined,
                      reference_id: filter?.seller
                        ? {
                            _eq: filter?.seller?.codename,
                          }
                        : undefined,
                    }
                  : undefined,
            }
          : undefined,
      },
    },
    pause: isUserWise,
  })

  const totalTrades = data?.advance_payment_aggregate?.aggregate?.count || 0

  const summary = isReceipt
    ? receiptSummaryData?.advance_summary_dashboard_receipt
    : summaryData?.advance_summary_dashboard
  const userWiseSummary = userWiseData?.department_user_advances?.summary
  const invoiceAmount = summary?.trade_amount || 0
  const requestedAdvance = summary?.requested_advance_amount || 0
  const settledAdvance = summary?.settled_amount || 0
  const releasedAdvance = summary?.released_advance_amount || 0

  const summaryCards = [
    {
      id: "invoice",
      title: isUserWise ? "Total Requested Amount" : "Invoice Amount",
      titleClassName: "!text-gray-800",
      value: Number(
        Number(
          isUserWise
            ? userWiseSummary?.total_requested_amount || 0
            : invoiceAmount
        ).toFixed(2)
      ),
      intentClass: "text-blue-400",
      iconBgClass: "bg-blue-100",
      icon: Receipt,
    },
    {
      id: "advance",
      title: isUserWise ? "Total Released Amount" : "Requested Advance Amount",
      titleClassName: "!text-gray-800",
      value: Number(
        Number(
          isUserWise
            ? userWiseSummary?.total_released_amount || 0
            : requestedAdvance
        ).toFixed(2)
      ),
      intentClass: isUserWise ? "text-green-400" : "text-blue-400",
      iconBgClass: isUserWise ? "bg-green-100" : "bg-blue-100",
      icon: isUserWise ? PaperPlane : Hand,
    },
    {
      id: "released",
      title: isUserWise ? "Total Settled Amount" : "Released Advance Amount",
      titleClassName: "!text-gray-800",
      value: Number(
        Number(
          isUserWise ? userWiseSummary?.used_amount || 0 : releasedAdvance
        ).toFixed(2)
      ),
      intentClass: "text-green-300",
      iconBgClass: "bg-green-100",
      icon: CurrencyInr,
    },
    {
      id: "settled",
      title: isUserWise ? "Total Remaining Amount" : "Settled Advance Amount",
      titleClassName: "!text-gray-800",
      value: Number(
        Number(
          isUserWise ? userWiseSummary?.remaining_amount || 0 : settledAdvance
        ).toFixed(2)
      ),
      intentClass: isUserWise ? "text-[#7B61FF]" : "text-green-300",
      iconBgClass: isUserWise ? "bg-[#EFECFF]" : "bg-green-100",
      icon: isUserWise ? Cardholder : CheckCircle,
    },
    {
      id: "pending",
      title: isUserWise ? "Unreleased Amount" : "Advance Balance Remaining",
      titleClassName: "!text-gray-800",
      value: Number(
        isUserWise
          ? Number(userWiseSummary?.unreleased_amount || 0).toFixed(2)
          : (Number(releasedAdvance) || 0) - (Number(settledAdvance) || 0)
      ).toFixed(2),
      intentClass: isUserWise ? "text-yellow-600" : "text-cyan-600",
      iconBgClass: isUserWise ? "bg-yellow-100" : "bg-cyan-100",
      icon: Wallet,
    },
  ]

  const onSubmitCloseAdvance = async () => {
    if (!closeAdvancePayment?.id) return
    const { error } = await closeAdvance({
      advance_payment_id: closeAdvancePayment?.id,
    })
    if (error) {
      return CustomToast({
        title: "Something went wrong! " + error.message,
        intent: "danger",
      })
    }
    refetch({ requestPolicy: "network-only" })
    setCloseAdvancePayment(undefined)
    return CustomToast({ title: "Advance request closed successfully!" })
  }

  const COLUMNS = [
    {
      Header: "Created On",
      accessor: "created_at",
      Cell({ value }) {
        // if (!value) return "--"
        return (
          <Cell
            title={dayjs(value).format("D MMM 'YY")}
            subtitle={dayjs(value).format("hh:mm a")}
          />
        )
      },
      formatter: () => {
        return "Total"
      },
    },
    {
      Header: <div className="pl-3">{isTrade ? "Trade Id" : "Receipt Id"}</div>,
      id: "id",
      Cell({ row: { original } }) {
        const value = isTrade ? original?.trade?.id : original?.receipt?.id
        if (!value) return "--"
        return (
          <div className="pl-4">
            <div
              className="link-text text-left w-fit cursor-pointer"
              onClick={() => {
                params.set("transaction", "true")

                params.set("advanceId", original?.id)
                setParams(params)
              }}
            >
              #{value}
            </div>
          </div>
        )
      },
    },
    {
      Header: "Type",
      id: "kind",
      Cell({ row: { original } }) {
        const value = isTrade ? original?.trade?.kind : original?.receipt?.kind
        const kind = TRADE_KINDS.find(k => k.id === value)
        if (!kind?.name) return "--"

        return <TradeKindTag kind={kind?.id as any} />
      },
    },

    {
      Header: "Supplier",
      accessor: "trade.seller.user[0]",
      id: "seller",
      Cell({ row: { original } }) {
        const value = isTrade
          ? original?.trade?.seller?.user?.[0]
          : original?.receipt?.seller?.user?.[0]
        if (!value) return "--"
        return (
          <Cell
            title={value.full_name}
            subtitle={
              <CodenameTag referenceId={value.external_reference_id2} />
            }
          />
        )
      },
    },

    {
      Header: "Advance Type",
      accessor: "parameter.name",
      Cell({ value }) {
        if (!value) return "--"
        return (
          <Tag intent="warning" variant="outline" className="!rounded-xl">
            {value}
          </Tag>
        )
      },
    },

    {
      Header: "Total Amount",
      accessor: isTrade ? "trade.seller_payable" : "receipt.seller_payable",
      Cell({ row: { original }, value }) {
        if (!value) return "--"
        return <Cell titleClassName="font-700 " title={inr(value)} />
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
    {
      Header: (
        <p>
          Requested Advance Amount{" "}
          <Tooltip
            content={
              "Advance amount can be less than or equal to the given amount, but cannot exceed it."
            }
          >
            <Info size={16} className="inline cursor-pointer text-blue" />
          </Tooltip>
        </p>
      ),
      accessor: "amount",
      Cell({ value }) {
        if (!value) return "--"
        return <Cell titleClassName="font-700 !text-green" title={inr(value)} />
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
    {
      Header: "Advance Status",
      accessor: "status",
      Cell({ value, row }) {
        const isStatusReleasePending =
          value === ADVANCE_STATUS.RELEASE_PARTIAL ||
          value === ADVANCE_STATUS.RELEASE_PENDING

        return (
          <Cell
            titleClassName={classNames(
              "flex gap-1 items-center font-600",
              value === ADVANCE_STATUS.DRAFT
                ? "!text-yellow"
                : value === ADVANCE_STATUS.REJECTED
                ? "!text-red"
                : value === ADVANCE_STATUS.RELEASE_COMPLETE
                ? "!text-green"
                : isStatusReleasePending
                ? "!text-blue"
                : value === ADVANCE_STATUS.REQUESTED
                ? "!text-blue"
                : ""
            )}
            title={
              <>
                <Circle weight="fill" />
                <span className="">
                  {value}
                  {isStatusReleasePending ? (
                    <Tooltip content="Please close request or release full amount to start advance utilization!">
                      <Info size={18} className="inline cursor-pointer ml-2" />
                    </Tooltip>
                  ) : null}
                </span>
              </>
            }
          />
        )
      },
    },
    {
      Header: "Released Amount",
      accessor: "transactions_aggregate.aggregate.sum.amount",
      Cell({ value }) {
        return (
          <Cell titleClassName="font-700 !text-green" title={inr(value || 0)} />
        )
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
    {
      Header: "Advance Balance Remaining",
      accessor: "remaining_amount",
      Cell({ value }) {
        return (
          <Cell titleClassName="font-700 !text-green" title={inr(value || 0)} />
        )
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
    {
      Header: "Placed By",
      accessor: "created_by",
      Cell({ value, row }) {
        if (!value) return "--"
        return (
          <Tooltip
            content={
              <div>
                <p>{value?.full_name || "na"}</p>
                <p className="text-gray-400">
                  {dayjs(row?.original?.created_at).format(
                    "DD MMM 'YY | h:mm a"
                  )}
                </p>
              </div>
            }
          >
            <Avatar
              className="relative text-center"
              name={value?.full_name}
              src={value?.photo?.url}
            />
          </Tooltip>
        )
      },
    },
    {
      Header: "Transaction Status",
      accessor: "settlement_status",
      Cell({ row: { original }, value }) {
        const paid = value === "Partial"
        const isSettled = value === "Complete"
        const pending = value === "Not Started"
        return (
          <Cell
            titleClassName={classNames(
              "flex gap-1 items-center font-600 ",
              isSettled
                ? "!text-green"
                : paid
                ? "!text-blue"
                : pending
                ? "!text-yellow"
                : "text-red"
            )}
            title={
              <>
                <Circle weight="fill" />
                {isSettled
                  ? "Settled"
                  : paid
                  ? "Partial"
                  : pending
                  ? "Pending"
                  : "Cancelled"}
              </>
            }
          />
        )
      },
    },
    {
      Header: "Last Transaction",
      accessor: "transactions.0.date",
      Cell({ value }) {
        if (!value) return "--"
        return <Cell title={<>{dayjs(value).format("DD MMM 'YYYY")}</>} />
      },
    },
    {
      Header: "Remark",
      accessor: "remark",
      Cell({ value, row: { original } }) {
        const parentReceiptId = original?.transactions?.[0]
          ?.parent_transaction_id
          ? original?.transactions?.[0]?.trade?.receipt_id
          : undefined
        return (
          <>{parentReceiptId ? `Settled Receipt #${parentReceiptId}` : value}</>
        )
      },
    },
    {
      Header: "",
      accessor: "updated_at",
      Cell({ row: { original } }) {
        const [params, setParams] = useSearchParams()

        return (
          <Popover
            minimal
            arrow={false}
            className="p-0"
            trigger="click"
            interactive
            placement="bottom"
            content={
              <Menu className="max-h-72">
                <MenuItem
                  onClick={() => {
                    params.set("remark", original.id)
                    setParams(params)
                  }}
                >
                  Remark
                </MenuItem>
                <MenuItem
                  disabled={original?.status === "Release Complete"}
                  onClick={() => {
                    setCloseAdvancePayment(original)
                  }}
                >
                  Close Advance Request
                </MenuItem>
              </Menu>
            }
          >
            <button className="hover:bg-gray-300 rounded">
              <DotsThreeVertical size={20} />
            </button>
          </Popover>
        )
      },
    },
  ]

  const USER_WISE_COLUMNS = [
    {
      Header: "Created On",
      accessor: "created_at",
      Cell({ value }) {
        if (!value) return "--"
        return (
          <Cell
            title={dayjs(value).format("D MMM 'YY")}
            subtitle={dayjs(value).format("hh:mm a")}
          />
        )
      },
      formatter: () => {
        return "Total"
      },
    },
    {
      Header: "Supplier",
      id: "supplier",
      Cell({ row: { original } }) {
        const value = original?.user
        if (!value) return "--"
        return (
          <Cell
            title={value?.name}
            subtitle={
              value?.reference_id ? (
                <CodenameTag referenceId={value?.reference_id} />
              ) : undefined
            }
          />
        )
      },
    },
    {
      Header: "Total Requested Amount",
      accessor: "total_requested_amount",
      Cell({ value }) {
        if (!value) return "--"
        return <Cell titleClassName="font-700 !text-green" title={inr(value)} />
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
    {
      Header: "Total Released Amount",
      accessor: "total_released_amount",
      Cell({ value }) {
        if (!value) return "--"
        return <Cell titleClassName="font-700 !text-green" title={inr(value)} />
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
    {
      Header: "Settle Amount",
      accessor: "used_amount",
      Cell({ value }) {
        if (!value) return "--"
        return <Cell titleClassName="font-700 !text-green" title={inr(value)} />
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
    {
      Header: "Remaining Amount",
      accessor: "remaining_amount",
      Cell({ value }) {
        if (!value) return "--"
        return <Cell titleClassName="font-700 !text-green" title={inr(value)} />
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
    {
      Header: "Unreleased Amount",
      accessor: "unreleased_amount",
      Cell({ value }) {
        if (!value) return "--"
        return <Cell titleClassName="font-700 !text-green" title={inr(value)} />
      },
      aggregate: "sum",
      formatter: inrFormatter.format,
    },
  ]

  return (
    <>
      <header className="grid grid-cols-3 justify-between p-2 border-b-1 border-b-gray-300">
        <SegmentedControl
          value={"advance"}
          onChange={value => {
            if (value === "net") {
              navigate(AppRoutes.netPayable)
            }
            if (value === "settlement") {
              navigate(AppRoutes.payableSettlement)
            }
          }}
          data={[
            {
              label: "Payable Settlement",
              value: "settlement",
              icon: <Receipt className="mr-1" />,
            },
            {
              label: "Advance",
              value: "advance",
              icon: <List className="mr-1" />,
            },
            {
              label: "Net Payable",
              value: "net",
              icon: <Receipt className="mr-1" />,
            },
          ].filter(b => b)}
        />
        <div className="w-full flex items-center gap-3">
          <InputGroup
            className={"w-full"}
            leftElement={<MagnifyingGlass />}
            inputProps={{
              value: search || "",
              placeholder: isUserWise
                ? "Search by Seller Name"
                : "Search by Patti no., Seller name",
              onChange(e) {
                params.set("search", e.target.value)
                params.delete("page")
                setParams(params)
              },
            }}
          />
          <PayableFilter
            filter={filter}
            setFilter={setFilter}
            hideDepartment
            hideHishobPatti
            hideInwardPatti
            hideStoragePatti
            hideInactive
            hidePlacedBy={isUserWise}
            hideAdvanceStatus={isUserWise}
            hideAdvanceType={isUserWise}
            hideTradeKind={isUserWise}
            hideTransactionStatus={isUserWise}
          />
        </div>
        <div className="flex gap-2 justify-end">
          {isShowImporter || canViewPayableReceivableReport ? (
            <PayableActions />
          ) : null}
          <PayableSelector withUserWise />
        </div>
      </header>
      <section className="m-4 bg-white p-4 mb-0 rounded-t-xl">
        <div className="grid grid-cols-5 gap-4">
          <TableSummary
            summary={summaryCards}
            loading={
              isUserWise ? userWiseFetching : fetching || summaryFetching
            }
          />
        </div>
      </section>
      <Note
        className="mx-4 my-2 border-l-4 border-blue text-[14px]"
        intent="info"
        title={
          (
            <div className="flex gap-2 items-center">
              <Info className="inline" size={15} weight="fill" />
              <p>
                <span className="font-700"> Child Patti </span>can be created
                only after the Sauda/Transit Patti advance is
                <span className="font-700"> Approved </span>or
                <span className="font-700"> Rejected</span>. If the advance
                remains{" "}
                <span className="font-700">
                  {" "}
                  Approval Pending, Child Patti creation is not allowed.
                </span>
              </p>
            </div>
          ) as any
        }
      />
      <RemarkModal
        refetch={refetch}
        title="Remark"
        useAddRemarkMutation={useUpdateAdvancePaymentMutation}
      />
      <AddTransaction
        refetch={refetch}
        isVerified={detailData?.advance_payment_by_pk?.is_verified}
        advanceStatus={detailData?.advance_payment_by_pk?.status}
        totalAmount={detailData?.advance_payment_by_pk?.amount}
        transactions={detailData?.advance_payment_by_pk?.transactions}
        tradeId={detailData?.advance_payment_by_pk?.trade?.id}
        receiptId={detailData?.advance_payment_by_pk?.receipt?.id}
        advanceAmount={detailData?.advance_payment_by_pk?.amount}
        createdAt={detailData?.advance_payment_by_pk?.created_at}
        headerTotalAmount={
          isTrade
            ? detailData?.advance_payment_by_pk?.trade?.seller_payable
            : detailData?.advance_payment_by_pk?.receipt?.seller_payable
        }
        remark={detailData?.advance_payment_by_pk?.remark}
        kind={
          isTrade
            ? detailData?.advance_payment_by_pk?.trade?.kind
            : detailData?.advance_payment_by_pk?.receipt?.kind
        }
        objectId={
          isTrade
            ? detailData?.advance_payment_by_pk?.trade?.id
            : detailData?.advance_payment_by_pk?.receipt?.id
        }
        parameterName={detailData?.advance_payment_by_pk?.parameter?.name}
        sellerName={
          isTrade
            ? detailData?.advance_payment_by_pk?.trade?.seller?.user?.[0]
                ?.full_name || ""
            : detailData?.advance_payment_by_pk?.receipt?.seller?.user?.[0]
                ?.full_name || ""
        }
        fetching={detailFetching}
        isTrade={isTrade}
        historyCols={`!grid-cols-5`}
        historyColSpan={`!col-span-4`}
        bankSelectorOptions={
          isTrade
            ? detailData?.advance_payment_by_pk?.trade?.seller?.user?.[0]
                ?.payment_details?.length !== 0
              ? detailData?.advance_payment_by_pk?.trade?.seller?.user?.[0]
                  ?.payment_details || []
              : detailData?.advance_payment_by_pk?.trade?.seller?.user?.[0]
                  ?.my_businesses?.[0]?.payment_details || []
            : detailData?.advance_payment_by_pk?.receipt?.seller?.user?.[0]
                ?.payment_details?.length !== 0
            ? detailData?.advance_payment_by_pk?.receipt?.seller?.user?.[0]
                ?.payment_details || []
            : detailData?.advance_payment_by_pk?.receipt?.seller?.user?.[0]
                ?.my_businesses?.[0]?.payment_details || []
        }
      />
      {isUserWise ? (
        <TableView
          relative={false}
          className="!mt-0 overflow-x-auto"
          tableClassName="!mt-0 !min-h-[570px] max-h-[570px] overflow-y-scroll"
          isLoading={userWiseFetching}
          columns={USER_WISE_COLUMNS}
          data={(userWiseData?.department_user_advances?.items || [])
            .filter(item => item !== null && item !== undefined)
            ?.map(it => {
              return {
                ...it,
                total_requested_amount: Number(it?.total_requested_amount),
                total_released_amount: Number(it?.total_released_amount),
                used_amount: Number(it?.used_amount),
                remaining_amount: Number(it?.remaining_amount),
                unreleased_amount: Number(it?.unreleased_amount),
              }
            })}
          meta={
            <>
              {userWiseData?.department_user_advances?.items?.length || 0}{" "}
              results
            </>
          }
          aggregateColumnClassName={
            "bg-blue-100 hover:bg-blue-100 p-10 sticky bottom-0 z-[8] font-600"
          }
        />
      ) : (
        <TableView
          relative={false}
          className="!mt-0 overflow-x-auto"
          tableClassName="!mt-0 !min-h-[570px] max-h-[570px] overflow-y-scroll"
          paginationProps={{
            total: Math.ceil(totalTrades / siteConfig.pageSize),
            page: page,
            onChange(page) {
              params.set("page", page.toString())
              params.set("search", "")
              setParams(params)
            },
          }}
          isLoading={fetching}
          columns={COLUMNS}
          data={data?.advance_payment || []}
          meta={<>{totalTrades} results</>}
          aggregateColumnClassName={
            "bg-blue-100 hover:bg-blue-100 p-10 sticky bottom-0 z-[8] font-600"
          }
        />
      )}
      <CloseAdvanceRequest
        isOpen={Boolean(closeAdvancePayment)}
        onClose={() => setCloseAdvancePayment(undefined)}
        onCancel={() => setCloseAdvancePayment(undefined)}
        onSubmit={onSubmitCloseAdvance}
        amount={closeAdvancePayment?.amount}
        tradeId={
          isTrade
            ? closeAdvancePayment?.trade?.id
            : closeAdvancePayment?.receipt?.id
        }
        releasedAmount={
          closeAdvancePayment?.transactions_aggregate?.aggregate?.sum?.amount
        }
        isTrade={isTrade}
      />
    </>
  )
}

export default AdvancePayable
